import { Grid, Typography, useTheme } from '@mui/material'
import { forwardRef, useCallback, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useDeleteFile } from 'src/api/file'
import { useDeleteFormSchemaReportById } from 'src/api/form-schema'
import DocumentViewer from 'src/components/common/content/DocumentViewer'
import { IFile } from 'src/generated-types'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { invalidateDeviation, useGetDeviationDocuments } from '../../../api/deviation'
import { useGetLocality } from '../../../api/localities'
import FormSchemasDialog from '../../../components/overallReport/FormSchemasDialog'
import ReportViewer from '../../../components/overallReport/ReportViewer'
import { DeviationDocumentType, IDeviationDocument } from '../../../generated-types/deviation-document'
import { formFieldsDefaultValues } from '../../../generated-types/form-field'
import { formSchemaReportTypes } from '../../../generated-types/form-schema'
import { CreateAttachment } from './CreateAttachment'

/** Imperative handle so the parent section card can drive the add dialogs from buttons in its header. */
export type DeviationDocumentsHandle = {
  openAddForm: () => void
  openAddAttachment: () => void
}

const DeviationDocuments = forwardRef<DeviationDocumentsHandle, any>(function DeviationDocuments(
  { customerId, localityId, deviationId, isDisabled, context },
  ref,
) {
  const { t } = useTranslation(['document', 'common', 'overall-reports'])
  const { data } = useGetDeviationDocuments(customerId, deviationId)
  const theme = useTheme()
  const { data: locality } = useGetLocality(customerId as EntityId, localityId as EntityId)

  const { mutateAsync: fileDeleteAsync } = useDeleteFile()
  const { mutateAsync: deleteReportAsync } = useDeleteFormSchemaReportById(customerId as EntityId)
  const { showDialog, showConfirmDialog, openFilePreviewDialog } = useDialogCtx()

  const handleDelete = async (id: number, type: DeviationDocumentType) => {
    showConfirmDialog(
      type === DeviationDocumentType.Document
        ? t('deleteDocument', { ns: 'document' })
        : t('deleteFormSchemaReportTitle', { ns: 'form-schema-report' }),
      type === DeviationDocumentType.Document
        ? t('documentDeleteConfirmation', { ns: 'document' })
        : t('deleteFormSchemaReportMessage', { ns: 'form-schema-report' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        if (type === DeviationDocumentType.Document) {
          await fileDeleteAsync(id, {
            onSuccess: () => {
              toast.success(t('document-deleted', { ns: 'document' }))
              invalidateDeviation.getDeviationDocuments(customerId as EntityId, deviationId as EntityId)
              pop()
            },
            onError: () => {
              toast.error(t('error-document-deleting', { ns: 'document' }))
            },
          })
        } else {
          await deleteReportAsync(id, {
            onSuccess: () => {
              toast.success(t('form-schema-report-deleted', { ns: 'form-schema-report' }))
              invalidateDeviation.getDeviationDocuments(customerId as EntityId, deviationId as EntityId)
              pop()
            },
            onError: () => {
              toast.error(t('error-deleting-form-schema-report', { ns: 'form-schema-report' }))
            },
          })
        }
      },
    )
  }

  const onAddFormSchema = useCallback(() => {
    showDialog(
      FormSchemasDialog,
      {
        componentProps: {
          customerId: customerId,
          property: {
            deviationId: deviationId,
            type: formSchemaReportTypes.Deviation,
          },
          formInitialValues: {
            [formFieldsDefaultValues.siteNumber]: locality?.locationNr,
            [formFieldsDefaultValues.siteLocationName]: locality?.name,
            [formFieldsDefaultValues.facilityName]: locality?.name,
          },
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }, [deviationId, customerId, locality])

  const onAddAttachment = useCallback(() => {
    showDialog(
      CreateAttachment,
      {
        componentProps: {
          customerId: customerId,
          deviationId: deviationId,
        },
      },
      undefined,
      true,
    )
  }, [deviationId, customerId])

  useImperativeHandle(
    ref,
    () => ({
      openAddForm: onAddFormSchema,
      openAddAttachment: onAddAttachment,
    }),
    [onAddFormSchema, onAddAttachment],
  )

  const onFileClick = callbackData => {
    const deviationDocument = callbackData as IDeviationDocument
    if (deviationDocument?.type == DeviationDocumentType.Document) {
      openFilePreviewDialog(deviationDocument?.file?.url)
    } else if (deviationDocument?.formSchemaReport?.isDraft) {
      showDialog(
        FormSchemasDialog,
        {
          componentProps: {
            customerId: customerId,
            property: {
              deviationId: deviationId,
              type: formSchemaReportTypes.Deviation,
            },
            formInitialValues: {
              [formFieldsDefaultValues.siteNumber]: locality?.locationNr,
              [formFieldsDefaultValues.siteLocationName]: locality?.name,
            },
            selectedSchemaForm: { id: deviationDocument?.formSchemaReport?.formSchemaVersion?.formSchemaId },
            draftReports: [deviationDocument?.formSchemaReport],
            selectedReportId: deviationDocument?.formSchemaReport?.id,
          },
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
    } else {
      showDialog(ReportViewer, {
        componentProps: {
          layouts: JSON.parse(deviationDocument?.formSchemaReport?.formSchemaVersion?.layout || ''),
          schema: deviationDocument?.formSchemaReport?.formSchemaVersion?.formSchema,
          report: deviationDocument?.formSchemaReport,
          createdAt: deviationDocument?.created,
          createdByUser: deviationDocument?.createdByUser,
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      })
    }
  }

  return data?.length !== 0 ? (
    <Grid container spacing={1.5}>
      {data?.map((document, index) => {
        return (
          <Grid item xs={12} sm={6} lg={4} key={document.id}>
            <DocumentViewer
              document={true}
              backgroundColor={
                index % 2 == 0
                  ? getThemeColor(theme, MUIThemeColors.infoLight)
                  : getThemeColor(theme, MUIThemeColors.white)
              }
              file={
                (document?.type == DeviationDocumentType.Document
                  ? { ...document?.file, ...{ createdByUser: document?.createdByUser } }
                  : {
                      contentType: 'application/pdf',
                      url: '',
                      filename: document?.formSchemaReport?.formSchemaVersion?.formSchema?.name,
                      contentLength: 0,
                      createdByUser: document?.createdByUser,
                    }) as IFile
              }
              enablePreview={false}
              disabled={isDisabled}
              callbackData={document}
              isDraft={document?.type === DeviationDocumentType.SchemaReport && document?.formSchemaReport?.isDraft}
              draftCaption={document?.formSchemaReport?.draftCaption}
              onFileClick={(e, file, callbackData) => {
                onFileClick(callbackData)
              }}
              onDelete={(e, file, callbackData) => {
                e.preventDefault()
                handleDelete(callbackData?.id, callbackData?.type)
              }}
              isDeletable={true}
            />
          </Grid>
        )
      })}
    </Grid>
  ) : (
    <Typography sx={{ fontSize: '13px', color: getThemeColor(theme, MUIThemeColors.secondaryMain) }}>
      {t('deviation-documents-empty-title', { ns: 'deviation' })}
    </Typography>
  )
})

export default DeviationDocuments
